import React, { useEffect, useState } from 'react';
import KycBioInput from './/KycBioInput';
import { FieldError } from './VgsInput/field';
import { DEFAULT_OWNERSHIP_VALUE } from './Ownership/const';
import { STATES } from '../const';
import { SnapIcon } from './SnapIcon';
import { SnapButton } from './SnapButton';

export type BeneficialOwnerData = {
  internalId: number;
  addressRegion?: string;
  percentOwnership?: number;
};

export default function BeneficialOwners({
  onValidChange,
  onChange,
  form,
  fieldErrors,
}: {
  onValidChange: (isValid: boolean) => void;
  onChange: (beneficialOwnersData: BeneficialOwnerData[]) => void;
  form: VGSForm<unknown>;
  fieldErrors: FieldError[];
}): JSX.Element {
  const [beneficialOwners, setBeneficialOwners] = useState<
    BeneficialOwnerData[]
  >([]);
  const [isValid, setIsValid] = useState(true);

  const handleAddOwnerButtonClick = (): void => {
    if (isValid) {
      setIsValid(false);
      const highestInternalId =
        beneficialOwners.length > 0
          ? Math.max(...beneficialOwners.map((bo) => bo.internalId))
          : 0;
      setBeneficialOwners([
        ...beneficialOwners,
        {
          internalId: highestInternalId + 1,
          percentOwnership: DEFAULT_OWNERSHIP_VALUE,
          addressRegion: STATES[0].value,
        },
      ]);
    }
  };

  const handleDeleteOwner = (id: number): void => {
    const entityName = `beneficialOwner${id}Kyc`;
    const FIELDS_TO_REMOVE = [
      'FirstName',
      'LastName',
      'Email',
      'Ssn',
      'PhoneNumber',
      'AddressStreet',
      'AddressUnit',
      'AddressCity',
      'AddressPostalCode',
      'DobDay',
      'DobMonth',
      'DobYear',
    ];

    for (const field of FIELDS_TO_REMOVE) {
      const fieldName = entityName + field;
      const formField = form.fields.find((f) => f.name === fieldName);
      formField?.delete();
    }
    setIsValid(true);
    setBeneficialOwners(beneficialOwners.filter((bo) => bo.internalId !== id));
  };

  const setBeneficialOwnerProp = (
    internalId: number,
    key: keyof BeneficialOwnerData,
    value: string | number,
  ): void => {
    const beneficialOwnersToSet = [...beneficialOwners];
    const beneficialOwner = beneficialOwnersToSet.find(
      (bo) => bo.internalId === internalId,
    );
    if (beneficialOwner === undefined) {
      throw new Error('Beneficial Owner not found');
    }
    if (key === 'addressRegion' && typeof value === 'string') {
      beneficialOwner.addressRegion = value;
    } else if (key === 'percentOwnership' && typeof value === 'number') {
      beneficialOwner.percentOwnership = value;
    }
    setBeneficialOwners(beneficialOwnersToSet);
  };

  useEffect(() => {
    if (isValid) {
      onChange(beneficialOwners);
    }
  }, [beneficialOwners, isValid, onChange]);

  const beneficialOwnersUi = beneficialOwners.map((beneficialOwner, index) => (
    <div key={beneficialOwner.internalId}>
      <h2 className='mb-2 text-lg font-semibold'>
        Additional Owner # {index + 1}
        <SnapIcon
          onClick={(): void => handleDeleteOwner(beneficialOwner.internalId)}
          icon='x-circle'
          color={'#94A3B8'}
          className='cursor-pointer'
        />
      </h2>

      <KycBioInput
        entityName={`beneficialOwner${beneficialOwner.internalId}`}
        onAddressRegionChange={(addressRegion: string): void => {
          setBeneficialOwnerProp(
            beneficialOwner.internalId,
            'addressRegion',
            addressRegion,
          );
        }}
        onPercentOwnershipChange={(percentOwnership: number): void => {
          setBeneficialOwnerProp(
            beneficialOwner.internalId,
            'percentOwnership',
            percentOwnership,
          );
        }}
        onValidChange={(newValidity: boolean): void => {
          if (newValidity !== isValid) {
            setIsValid(newValidity);
            onValidChange(newValidity);
          }
        }}
        form={form}
        askIsOwner={false}
        askPercentOwnershipOnLoad={true}
        askTitle={false}
        fieldErrors={fieldErrors}
      />
      <br />
      <hr />
      <br />
    </div>
  ));

  return (
    <div>
      <div className='w-full'>
        <div>
          {beneficialOwnersUi.length > 0 && (
            <div className='w-full'>
              <br />
              <hr />
              <br />
              {beneficialOwnersUi}
            </div>
          )}
          <p className='md:w-full my-2'>
            Add the personal information of anyone other than yourself who owns
            25% or more of the business.
          </p>
          <SnapButton
            onClick={handleAddOwnerButtonClick}
            disabled={!isValid}
            className={isValid ? '' : 'cursor-not-allowed opacity-50'}
          >
            <SnapIcon icon={'plus-solid'} size='xs' /> Add Owner
          </SnapButton>
        </div>
      </div>
    </div>
  );
}
